import { useEffect } from "react";
import type { ReactNode } from "react";
import { ThemeProvider as ScThemeProvider } from "styled-components";
import { GlobalStyle } from "./GlobalStyle";
import { lightTheme } from "./lightTheme";
import { darkTheme } from "./darkTheme";
import { devWarn, isDevelopment } from "../utils/devWarning";

export type ThemeMode = "light" | "dark";

type Props = {
  mode: ThemeMode;
  children: ReactNode;
};

export const ThemeProvider = ({ mode, children }: Props) => {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute("data-c6-theme");
    root.setAttribute("data-c6-theme", mode);
    return () => {
      if (previous === null) {
        root.removeAttribute("data-c6-theme");
      } else {
        root.setAttribute("data-c6-theme", previous);
      }
    };
  }, [mode]);

  // dev-only: typography.family.body 기본값은 Pretendard. 소비처가 로드를 잊으면 silent
  // fallback(system-ui)이라 눈치채기 어렵다 → 한 번 경고. fix는 `import ".../fonts"` 또는 자체 로드.
  //
  // FontFaceSet.check()는 안 쓴다 — 매칭 @font-face가 아예 없으면(=정확히 "로드 깜빡"한 케이스)
  // vacuously true를 반환해 정작 잡아야 할 미로드에서 침묵한다. 대신 등록된 FontFace를
  // 직접 훑어 "Pretendard"가 loaded인지 본다. false 방향은 안전한 쪽: dev 머신에
  // 시스템 설치만 돼 있으면 FontFace가 없어 경고가 뜰 수 있다(명시 로드 넛지). 의도적 override도 동일.
  useEffect(() => {
    // devWarn이 dev gate지만, prod에선 ready 리스너 자체를 안 달도록 일찍 빠진다. isDevelopment는
    // process 부재를 non-dev로 취급 → bundler shim 없는 prod 브라우저에서도 경고 안 뜸.
    if (!isDevelopment()) return;
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    if (!fonts?.ready || typeof fonts.forEach !== "function") return;
    let cancelled = false;
    void fonts.ready.then(() => {
      if (cancelled) return;
      let loaded = false;
      fonts.forEach((face) => {
        if (face.status === "loaded" && face.family.replace(/^["']|["']$/g, "") === "Pretendard") {
          loaded = true;
        }
      });
      if (loaded) return;
      devWarn(
        '[c6] typography.family.body 기본 폰트 "Pretendard"가 로드되지 않은 것 같습니다 — ' +
          '소비 앱에서 `import "@carbon6/c6-frontend-design/fonts"` 하거나 Pretendard를 직접 로드하세요. ' +
          "(family를 의도적으로 override했다면 무시하세요.)",
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ScThemeProvider theme={mode === "dark" ? darkTheme : lightTheme}>
      <GlobalStyle />
      {children}
    </ScThemeProvider>
  );
};
